import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isMobileBrowser } from '../../../base/environment/utils';
import { getLocalParticipant, isLocalParticipantModerator } from '../../../base/participants/functions';
import { NARROW_LAYOUT_THRESHOLD } from '../../../base/responsive-ui/constants';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { HANGUP_MENU_MAX_WIDTH, TOOLBAR_HORIZONTAL_PADDING } from '../../../meeting-ui/constants';
import { isReactionsButtonEnabled, shouldDisplayReactionsButtons } from '../../../reactions/functions.web';
import { isCCTabEnabled } from '../../../subtitles/functions.any';
import { isTranscribing } from '../../../transcribing/functions';
import { setHangupMenuVisible, setOverflowMenuVisible, setToolbarHovered, setToolboxVisible } from '../../actions.web';
import { getJwtDisabledButtons, getVisibleButtons, getVisibleButtonsForNarrowLayout, getVisibleButtonsForReducedUI, isButtonEnabled, isToolboxVisible, shouldShowToolbarQuickInput } from '../../functions.web';
import { useKeyboardShortcuts, useToolboxButtons } from '../../hooks.web';
import { EndConferenceButton } from './EndConferenceButton';
import HangupMenuButton from './HangupMenuButton';
import { LeaveConferenceButton } from './LeaveConferenceButton';
import MobileToolboxShortcuts from './MobileToolboxShortcuts';
import OverflowMenuButton from './OverflowMenuButton';
import Separator from './Separator';
import ToolbarQuickInput from './ToolbarQuickInput';
const useStyles = makeStyles()(theme => {
    const hangupMenuMaxViewportWidth = `calc(100vw - ${TOOLBAR_HORIZONTAL_PADDING * 2}px)`;
    return {
        hangupMenu: {
            // 用 !important 压过 ContextMenu 默认 absolute+right，避免相对视口再偏移导致裁切
            position: 'relative !important',
            right: 'auto !important',
            left: 'auto !important',
            top: 'auto !important',
            display: 'flex',
            flexDirection: 'column',
            rowGap: '8px',
            margin: '0 !important',
            marginTop: '0 !important',
            maxWidth: `min(${HANGUP_MENU_MAX_WIDTH}px, ${hangupMenuMaxViewportWidth})`,
            minWidth: 220,
            padding: '16px',
            marginBottom: '0 !important',
            boxSizing: 'border-box'
        },
        hangupOutline: {
            backgroundColor: 'transparent !important',
            border: `1px solid ${theme.palette.ui03 || '#D0D0D0'}`,
            color: `${theme.palette.text01} !important`
        }
    };
});
/**
 * 主底栏。挂断大按钮在底栏最右侧；点击后菜单在按钮上方弹出。
 *
 * @param {IProps} props - The props of the component.
 * @returns {ReactElement}
 */
export default function Toolbox({ toolbarButtons, toolbarBackgroundColor: toolbarBackgroundColorProp }) {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const _toolboxRef = useRef(null);
    const conference = useSelector((state) => state['features/base/conference'].conference);
    const isNarrowLayout = useSelector((state) => state['features/base/responsive-ui'].isNarrowLayout);
    const videoSpaceWidth = useSelector((state) => state['features/base/responsive-ui'].videoSpaceWidth);
    const isModerator = useSelector(isLocalParticipantModerator);
    const customToolbarButtons = useSelector((state) => state['features/base/config'].customToolbarButtons);
    const iAmRecorder = useSelector((state) => state['features/base/config'].iAmRecorder);
    const iAmSipGateway = useSelector((state) => state['features/base/config'].iAmSipGateway);
    const overflowDrawer = useSelector((state) => state['features/toolbox'].overflowDrawer);
    const shiftUp = useSelector((state) => state['features/toolbox'].shiftUp);
    const overflowMenuVisible = useSelector((state) => state['features/toolbox'].overflowMenuVisible);
    const hangupMenuVisible = useSelector((state) => state['features/toolbox'].hangupMenuVisible);
    const buttonsWithNotifyClick = useSelector((state) => state['features/toolbox'].buttonsWithNotifyClick);
    const reduxToolbarButtons = useSelector((state) => state['features/toolbox'].toolbarButtons);
    const toolbarButtonsToUse = toolbarButtons || reduxToolbarButtons;
    const isDialogVisible = useSelector((state) => Boolean(state['features/base/dialog'].component));
    const localParticipant = useSelector(getLocalParticipant);
    const transcribing = useSelector(isTranscribing);
    const _isCCTabEnabled = useSelector(isCCTabEnabled);
    const toolbarBackgroundColorFromConfig = useSelector((state) => state['features/base/config'].toolbarConfig?.backgroundColor);
    const toolbarAlwaysVisible = useSelector((state) => Boolean(state['features/base/config'].toolbarConfig?.alwaysVisible));
    const toolbarBackgroundColor = toolbarBackgroundColorProp || toolbarBackgroundColorFromConfig;
    const jwtDisabledButtons = getJwtDisabledButtons(transcribing, _isCCTabEnabled, localParticipant?.features);
    const reactionsButtonEnabled = useSelector(isReactionsButtonEnabled);
    const _shouldDisplayReactionsButtons = useSelector(shouldDisplayReactionsButtons);
    const toolbarVisible = useSelector(isToolboxVisible);
    const mainToolbarButtonsThresholds = useSelector((state) => state['features/toolbox'].mainToolbarButtonsThresholds);
    const { reducedUImainToolbarButtons, narrowMainToolbarButtons } = useSelector((state) => state['features/base/config']);
    const reducedUI = useSelector((state) => state['features/base/responsive-ui'].reducedUI);
    const allButtons = useToolboxButtons(customToolbarButtons);
    const isMobile = isMobileBrowser();
    // 宽度兜底：避免仅依赖 isNarrowLayout 订阅时序导致手机仍走宽屏收起档（只剩麦/摄像头）。
    const isWidthNarrow = videoSpaceWidth < NARROW_LAYOUT_THRESHOLD;
    const isNarrowOrMobile = isMobile || isNarrowLayout || isWidthNarrow;
    const endConferenceSupported = Boolean(conference?.isEndConferenceSupported() && isModerator);
    useKeyboardShortcuts(toolbarButtonsToUse);
    useEffect(() => {
        if (!toolbarVisible) {
            if (document.activeElement instanceof HTMLElement
                && _toolboxRef.current?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        }
    }, [toolbarVisible]);
    const onSetHangupVisible = useCallback((visible) => {
        dispatch(setHangupMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    const onSetOverflowVisible = useCallback((visible) => {
        dispatch(setOverflowMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    useEffect(() => {
        if (endConferenceSupported && isMobile) {
            hangupMenuVisible && dispatch(setToolboxVisible(true));
        }
        else if (hangupMenuVisible && !toolbarVisible) {
            onSetHangupVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [dispatch, endConferenceSupported, hangupMenuVisible, isMobile, toolbarVisible, onSetHangupVisible]);
    useEffect(() => {
        if (overflowMenuVisible && isDialogVisible) {
            onSetOverflowVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [dispatch, overflowMenuVisible, isDialogVisible, onSetOverflowVisible]);
    const onEscKey = useCallback((e) => {
        if (e?.key === 'Escape') {
            e?.stopPropagation();
            hangupMenuVisible && dispatch(setHangupMenuVisible(false));
            overflowMenuVisible && dispatch(setOverflowMenuVisible(false));
        }
    }, [dispatch, hangupMenuVisible, overflowMenuVisible]);
    const onMouseOut = useCallback(() => {
        !overflowMenuVisible && dispatch(setToolbarHovered(false));
    }, [dispatch, overflowMenuVisible]);
    const onMouseOver = useCallback(() => {
        dispatch(setToolbarHovered(true));
    }, [dispatch]);
    const handleFocus = useCallback(() => {
        dispatch(setToolboxVisible(true));
    }, [dispatch]);
    const handleBlur = useCallback((event) => {
        if (toolbarAlwaysVisible) {
            return;
        }
        const nextTarget = event.relatedTarget;
        if (nextTarget && event.currentTarget.contains(nextTarget)) {
            return;
        }
        dispatch(setToolboxVisible(false));
    }, [dispatch, toolbarAlwaysVisible]);
    if (iAmRecorder || iAmSipGateway) {
        return null;
    }
    const rootClassNames = `new-toolbox ${toolbarVisible ? 'visible' : ''} ${toolbarButtonsToUse.length ? '' : 'no-buttons'}`;
    const toolbarAccLabel = 'toolbar.accessibilityLabel.moreActionsMenu';
    const containerClassName = `toolbox-content${isNarrowOrMobile ? ' toolbox-content-mobile' : ''}`;
    const normalUIButtons = getVisibleButtons({
        allButtons,
        buttonsWithNotifyClick,
        toolbarButtons: toolbarButtonsToUse,
        clientWidth: videoSpaceWidth,
        jwtDisabledButtons,
        mainToolbarButtonsThresholds
    });
    const narrowUIButtons = getVisibleButtonsForNarrowLayout({
        allButtons,
        buttonsWithNotifyClick,
        jwtDisabledButtons,
        narrowMainToolbarButtons,
        toolbarButtons: toolbarButtonsToUse
    });
    const reducedUIButtons = getVisibleButtonsForReducedUI({
        allButtons,
        buttonsWithNotifyClick,
        jwtDisabledButtons,
        reducedUImainToolbarButtons,
    });
    let mainMenuButtons;
    let overflowMenuButtons;
    if (reducedUI) {
        mainMenuButtons = reducedUIButtons.mainMenuButtons;
        overflowMenuButtons = [];
    }
    else if (isNarrowOrMobile) {
        mainMenuButtons = narrowUIButtons.mainMenuButtons;
        overflowMenuButtons = narrowUIButtons.overflowMenuButtons;
    }
    else {
        mainMenuButtons = normalUIButtons.mainMenuButtons;
        overflowMenuButtons = normalUIButtons.overflowMenuButtons;
    }
    const raiseHandInOverflowMenu = overflowMenuButtons.some(({ key }) => key === 'raisehand');
    const showReactionsInOverflowMenu = _shouldDisplayReactionsButtons
        && ((!reactionsButtonEnabled && (raiseHandInOverflowMenu || isNarrowOrMobile))
            || overflowMenuButtons.some(({ key }) => key === 'reactions'));
    const showRaiseHandInReactionsMenu = showReactionsInOverflowMenu && raiseHandInOverflowMenu;
    const leftToolbarKeys = new Set(['raisehand']);
    const showQuickInput = shouldShowToolbarQuickInput(videoSpaceWidth, isNarrowOrMobile, mainToolbarButtonsThresholds);
    const centerMainButtons = isNarrowOrMobile
        ? mainMenuButtons.filter(({ Content }) => Content !== Separator)
        : mainMenuButtons.filter(({ key, Content }) => Content !== Separator && !leftToolbarKeys.has(key));
    const leftMainButtons = isNarrowOrMobile
        ? []
        : mainMenuButtons.filter(({ key, Content }) => Content !== Separator && leftToolbarKeys.has(key));
    const toolbarLabelProps = { toolbarLabelBelow: true };
    // 窄屏/真机：挂断用顶栏右上角「结束」；仅宽屏底栏最右侧挂断。
    const showBottomHangup = !isNarrowOrMobile && isButtonEnabled('hangup', toolbarButtonsToUse);
    const renderToolbarButton = ({ Content, key, ...rest }) => (React.createElement(Content, { ...rest, ...toolbarLabelProps, buttonKey: key, key: key }));
    return (React.createElement("div", { className: cx(rootClassNames, shiftUp && 'shift-up'), id: 'new-toolbox', style: toolbarBackgroundColor ? { backgroundColor: toolbarBackgroundColor } : undefined },
        React.createElement("div", { className: containerClassName },
            React.createElement("div", { className: 'toolbox-content-wrapper', onBlur: handleBlur, onFocus: handleFocus, ...(isMobile ? {} : {
                    onMouseOut,
                    onMouseOver
                }) },
                isNarrowOrMobile && React.createElement(MobileToolboxShortcuts, null),
                React.createElement("div", { className: 'toolbox-content-items', ref: _toolboxRef },
                    !isNarrowOrMobile && (React.createElement("div", { className: 'toolbox-section-left' },
                        showQuickInput && React.createElement(ToolbarQuickInput, null),
                        leftMainButtons.map(renderToolbarButton))),
                    React.createElement("div", { className: isNarrowOrMobile
                            ? 'toolbox-section-center toolbox-section-narrow-main'
                            : 'toolbox-section-center' },
                        centerMainButtons.map(renderToolbarButton),
                        Boolean(overflowMenuButtons.length) && (React.createElement(OverflowMenuButton, { ariaControls: 'overflow-menu', ariaLabel: t('toolbar.accessibilityLabel.moreActionsMenu'), buttons: overflowMenuButtons.reduce((acc, val) => {
                                if (val.key === 'reactions' && showReactionsInOverflowMenu) {
                                    return acc;
                                }
                                if (val.key === 'raisehand' && showRaiseHandInReactionsMenu) {
                                    return acc;
                                }
                                if (acc.length) {
                                    const prev = acc[acc.length - 1];
                                    const group = prev[prev.length - 1].group;
                                    if (group === val.group) {
                                        prev.push(val);
                                    }
                                    else {
                                        acc.push([val]);
                                    }
                                }
                                else {
                                    acc.push([val]);
                                }
                                return acc;
                            }, []), isOpen: overflowMenuVisible, key: 'overflow-menu', onToolboxEscKey: onEscKey, onVisibilityChange: onSetOverflowVisible, showRaiseHandInReactionsMenu: showRaiseHandInReactionsMenu, showReactionsMenu: showReactionsInOverflowMenu, ...toolbarLabelProps }))),
                    showBottomHangup && (React.createElement("div", { className: 'toolbox-section-right' },
                        React.createElement(HangupMenuButton, { ariaControls: 'hangup-menu', ariaLabel: t('toolbar.accessibilityLabel.hangup'), isOpen: hangupMenuVisible, key: 'hangup-menu', notifyMode: buttonsWithNotifyClick?.get('hangup-menu'), onVisibilityChange: onSetHangupVisible, ...toolbarLabelProps },
                            React.createElement(ContextMenu, { accessibilityLabel: t(toolbarAccLabel), className: classes.hangupMenu, hidden: false, inDrawer: overflowDrawer, onKeyDown: onEscKey },
                                endConferenceSupported && (React.createElement(EndConferenceButton, { buttonKey: 'end-meeting', notifyMode: buttonsWithNotifyClick?.get('end-meeting') })),
                                React.createElement(LeaveConferenceButton, { buttonKey: 'hangup', buttonType: endConferenceSupported
                                        ? BUTTON_TYPES.TERTIARY
                                        : BUTTON_TYPES.DESTRUCTIVE, className: endConferenceSupported ? classes.hangupOutline : undefined, notifyMode: buttonsWithNotifyClick?.get('hangup') }))))))))));
}
